import React, { useRef, useCallback, useEffect, useState } from 'react';
import { FileUp } from 'lucide-react';
import { DocumentContent } from '../types';

interface UploadDocumentsProps {
  onFilesAdded: (documents: DocumentContent[]) => void;
}

export const UploadDocuments: React.FC<UploadDocumentsProps> = ({ onFilesAdded }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isPasting, setIsPasting] = useState(false);

  // Helper function to convert MIME type to file extension
  const getFileExtension = (mimeType: string, fileName: string): string => {
    // First try to get extension from filename
    const fileNameExtension = fileName.split('.').pop()?.toLowerCase();
    if (fileNameExtension) {
      return fileNameExtension;
    }

    // Fallback to MIME type mapping
    const mimeToExtension: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'application/pdf': 'pdf',
      'text/plain': 'txt',
    };

    return mimeToExtension[mimeType] || 'unknown';
  };

  const processFiles = useCallback(async (fileList: File[]) => {
    const validFiles = fileList.filter(file => {
      // Check if it's a supported document/image file
      return file.type.startsWith('image/') || 
             file.type === 'application/pdf' ||
             file.type === 'text/plain' ||
             ['png', 'jpg', 'jpeg', 'gif', 'webp', 'pdf', 'txt'].includes(
               file.name.split('.').pop()?.toLowerCase() || ''
             );
    });

    if (validFiles.length === 0) {
      alert('Please select supported files only (PNG, JPEG, PDF, TXT).');
      return;
    }

    if (validFiles.length !== fileList.length) {
      alert('Some files were skipped. Only images, PDFs, and text files are supported.');
    }

    const documents: DocumentContent[] = [];
    
    for (const file of validFiles) {
      try {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const fileExtension = getFileExtension(file.type, file.name);
        
        documents.push({
          name: file.name || `document_${Date.now()}.${fileExtension}`,
          format: fileExtension,
          content: base64,
        });
      } catch (error) {
        console.error('Error processing file:', file.name, error);
        alert(`Error processing file: ${file.name}`);
      }
    }

    if (documents.length > 0) {
      onFilesAdded(documents);
    }
  }, [onFilesAdded]);

  const processClipboardFiles = useCallback(async (items: DataTransferItemList) => {
    const files: File[] = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }
    
    if (files.length > 0) {
      await processFiles(files);
    }
  }, [processFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  }, [processFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    e.preventDefault();
    setIsPasting(true);
    
    try {
      await processClipboardFiles(e.clipboardData.items);
    } finally {
      setTimeout(() => setIsPasting(false), 500);
    }
  }, [processClipboardFiles]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
    // Reset the input value so the same file can be selected again
    if (e.target) {
      e.target.value = '';
    }
  }, [processFiles]);

  // Set up global paste event listener
  useEffect(() => {
    const handleGlobalPaste = async (e: ClipboardEvent) => {
      if (e.clipboardData?.items) {
        setIsPasting(true);
        try {
          await processClipboardFiles(e.clipboardData.items);
        } finally {
          setTimeout(() => setIsPasting(false), 500);
        }
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => document.removeEventListener('paste', handleGlobalPaste);
  }, [processClipboardFiles]);

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer
          ${isDragOver 
            ? 'border-primary bg-primary/10' 
            : 'border-base-300 hover:border-primary hover:bg-primary/5'
          }
          ${isPasting ? 'animate-pulse' : ''}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onPaste={handlePaste}
        onClick={handleClick}
        tabIndex={0}
        role="button"
        aria-label="Upload files by dragging, clicking, or pasting"
      >
        <div className="max-w-md mx-auto">
          <div className="mb-4">
            <FileUp className="w-12 h-12 text-base-content/50 mx-auto" />
          </div>
          
          <h3 className="text-lg font-medium text-base-content mb-2">
            Upload Documents
          </h3>
          
          <p className="text-sm text-base-content/70 mb-4">
            Drag and drop files, paste images, or click to browse
          </p>
          
          <div className="text-xs text-base-content/50">
            <kbd className="px-2 py-1 bg-base-200 rounded text-xs">Ctrl+V</kbd> to paste images
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.txt,text/plain,application/pdf"
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
}; 